import {
  Bike,
  Activity,
  Trees,
  Users,
  Palette,
  Coffee,
  Target,
  Layout,
  Briefcase,
  Smile,
  LucideIcon,
} from 'lucide-react'

export interface Skill {
  id: string
  label: string
  description: string
}

export interface Assignment {
  id: number
  category: string
  title: string
  period: string
  description: string
  tags: string[]
}

export interface PersonalFact {
  icon: LucideIcon
  title: string
  text: string
}

export interface AreaCard {
  num: string
  title: string
  desc: string
  color: 'terracotta' | 'sage'
  href: string
}

export interface CTAPersona {
  icon: LucideIcon
  title: string
  text: string
}

export const skills: Skill[] = [
  {
    id: 'affarsutveckling',
    label: 'Affärsutveckling',
    description:
      'Affärsmodeller, lönsamhetsanalys, budgetering och kundstrategi. Har arbetat med allt från nyöppning av klinik till IT-konsultbolag och familjeföretag.',
  },
  {
    id: 'forandringsledning',
    label: 'Förändringsledning',
    description:
      'Lean change management, iterativa processer och Prosci ADKAR. Har lett förändringsarbete i allt från kärnkraftverk till startups.',
  },
  {
    id: 'ekonomisk-styrning',
    label: 'Ekonomisk styrning',
    description:
      'Kassaflöde, likviditet, prognoser och scenarioanalys. Följde kassaflödet dagligen som extern VD — inte för att det var kul, utan för att det var nödvändigt.',
  },
  {
    id: 'facilitering',
    label: 'Facilitering',
    description:
      'Workshops, Open Space, beslutsprocesser och tvärfunktionella grupper. Har faciliterat allt från innovationssessioner till svåra prioriteringsdiskussioner.',
  },
  {
    id: 'ledarskap',
    label: 'Ledarskap & coaching',
    description:
      'ICF-PCC certifierad coach. Chefsutveckling, konflikthantering och svåra samtal. Har tränat chefer i att ge feedback de egentligen inte ville ge.',
  },
  {
    id: 'organisationsdesign',
    label: 'Organisationsdesign',
    description:
      'Teamstruktur, mandat, beslutsvägar och samverkan. Specialiserad på att bygga strukturer som faktiskt håller när organisationen växer.',
  },
]

export const assignments: Assignment[] = [
  {
    id: 1,
    category: 'Extern VD',
    title: 'Redovisningsbyrå i tillväxtfas',
    period: '2021–2023',
    description:
      'Fullt affärs- och resultatansvar. Balanserade expansionsambitioner mot lönsamhet och likviditet. Följde kassaflödet dagligen.',
    tags: ['Tillväxt', 'Lönsamhet', 'Kassaflöde'],
  },
  {
    id: 2,
    category: 'Förändring',
    title: 'Tekniskt komplex reglerad verksamhet',
    period: '2023–2025',
    description:
      'Stärkte genomförandeförmåga och flöde i en organisation med höga säkerhets- och lönsamhetskrav. Ledde tvärfunktionella arbetsgrupper.',
    tags: ['Genomförande', 'Tvärfunktionellt', 'Säkerhet'],
  },
  {
    id: 3,
    category: 'Förändring',
    title: 'Produktutvecklingsavdelning',
    period: '2022',
    description:
      'Analyserade arbetsflöden, faciliterade dialoger och skapade samsyn kring uppdrag och positionering för ca 30 personer.',
    tags: ['Struktur', 'Samverkan', 'Värdeskapande'],
  },
  {
    id: 4,
    category: 'Förändring',
    title: 'Evenemangsbransch under pandemi',
    period: '2020–2021',
    description:
      'Stödde omställning från fysiska event till hybrid och digitalt. Etablerade tvärfunktionella processer och projektstruktur.',
    tags: ['Hybrid', 'Omställning', 'Processer'],
  },
  {
    id: 5,
    category: 'Utbildning',
    title: 'Förändringsledning, chefer & ledare',
    period: '2021–2022',
    description:
      'Utbildade chefer och ledare i iterativ förändringsledning. Kopplade teori till praktiskt genomförande i komplexa miljöer.',
    tags: ['Ledarskap', 'Iterativt', 'Genomförande'],
  },
  {
    id: 6,
    category: 'Utbildning',
    title: 'Agilt ledarskap, systemutvecklare',
    period: '2023',
    description:
      'Tränade blivande systemutvecklare i hur team skapar värde genom struktur, iteration och samarbete.',
    tags: ['Agilt', 'Team', 'Värde'],
  },
  {
    id: 7,
    category: 'Rådgivning',
    title: 'SME & ägarledda bolag',
    period: '2014–2022',
    description:
      'Rådgivning till små och medelstora företag i uppstart, tillväxt och ägarförändring. Affärsmodeller, lönsamhet och organisationsstruktur.',
    tags: ['Affärsmodell', 'Tillväxt', 'Ägarledda'],
  },
  {
    id: 8,
    category: 'Rådgivning',
    title: 'Innovationsprocesser, stora bolag',
    period: '2009–2013',
    description:
      'Ledde strukturerade innovationsprocesser från problemdefinition till koncept. Kunder inom fordon, media, livsmedel och hälsa.',
    tags: ['Innovation', 'Facilitering', 'Koncept'],
  },
]

export const personalFacts: PersonalFact[] = [
  {
    icon: Bike,
    title: 'MTB-entusiast',
    text: 'Cyklar gärna mountainbike. Helst i nedförsbacke och fort. Uppförsbackarna är mest ett nödvändigt ont.',
  },
  {
    icon: Activity,
    title: 'Tränar som en elitidrottare*',
    text: '* Försöker. Får mest träna rehab. Men ambitionen finns kvar.',
  },
  {
    icon: Trees,
    title: 'Skogsmänniska',
    text: 'Trivs ute i skogen, gärna på längre utflykter med långt att gå och god matsäck med.',
  },
  {
    icon: Users,
    title: 'Pappa till två',
    text: 'Har fått ovärderlig träning i konfliktlösning, förhandling och att hålla sig lugn under press.',
  },
  {
    icon: Palette,
    title: 'Gift med en konstnär',
    text: 'Min fru är konstnär och dekormålare — kan förvandla fysiska saker ungefär som man promptar en AI. Imponerande och lite skrämmande.',
  },
  {
    icon: Coffee,
    title: 'Hembryggare',
    text: 'Brygger egen öl. Mina vänner dricker upp den fort. Jag väljer att tolka det som en komplimang.',
  },
]

export const subtitles = [
  'Förändringsledare.',
  'Extern VD.',
  'Facilitator.',
  'Coach.',
]

export const ctaPersonas: CTAPersona[] = [
  {
    icon: Coffee,
    title: 'Ett första samtal',
    text: 'Du vill bara prata och se om kemin stämmer.',
  },
  {
    icon: Target,
    title: 'Perspektiv på en utmaning',
    text: 'Du har en specifik situation och vill bolla den.',
  },
  {
    icon: Layout,
    title: 'Nyfiken på ett område',
    text: 'Du vill veta om något av mina områden passar din verklighet.',
  },
  {
    icon: Briefcase,
    title: 'Veta mer om hur det fungerar',
    text: 'Praktiskt — vad innebär en dag i veckan egentligen?',
  },
  {
    icon: Users,
    title: 'Höra vad andra säger',
    text: 'Du vill ha referenser eller prata med någon som jobbat med mig.',
  },
  {
    icon: Smile,
    title: 'Bara nyfiken',
    text: 'Inget specifikt. Du vill veta mer. Det räcker.',
  },
]

export const areaCards: AreaCard[] = [
  {
    num: '01',
    title: 'Leda utvecklingen',
    desc: 'För dig som VD eller ägare som fastnat i det operativa och behöver strategisk drivkraft.',
    color: 'terracotta',
    href: '/omrade-1',
  },
  {
    num: '02',
    title: 'Från ett team till tre',
    desc: 'När organisationen växer men strukturen inte hänger med. Från familj till by.',
    color: 'sage',
    href: '/omrade-2',
  },
  {
    num: '03',
    title: 'Hybridarbete',
    desc: 'Ni har bestämt var ni jobbar. Jag hjälper er designa hur arbetet faktiskt fungerar.',
    color: 'terracotta',
    href: '/omrade-3',
  },
]
